import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  variant?: 'parent' | 'child';
  escapeKey?: boolean;
  uuid?: string;
}

export const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  children,
  className,
  style,
  variant = 'parent',
  uuid = '',
  escapeKey = false,
}) => {
  const [width, setWidth] = useState<string>('60%');
  const [isDragging, setIsDragging] = useState(false);
  // Track previous open value in state so we can reset width on open
  // without calling setState inside an effect. This is React's recommended
  // "adjusting state when a prop changes" pattern:
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  const [prevOpen, setPrevOpen] = useState(open);

  const isParent = variant === 'parent';
  const initialWidth = isParent ? '60%' : 'calc(50% - 60px)';
  const zIndex = isParent ? 50 : 51;

  // Reset width when drawer transitions from closed to open (during render).
  if (prevOpen !== open) {
    setPrevOpen(open);
    if (open && (variant === 'child' || !width)) {
      setWidth(initialWidth);
    }
  }

  // Handle dragging to resize the drawer
  const startResizing = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);

    const drawerElement = e.currentTarget.parentElement;
    if (!drawerElement) return;

    const currentWidth = drawerElement.getBoundingClientRect().width;
    const startX = e.pageX;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = startX - e.pageX;
      const newWidth = Math.max(
        300,
        Math.min(window.innerWidth * 0.8, currentWidth + deltaX)
      );
      setWidth(`${newWidth.toString()}px`);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const computedStyle = {
    ...style,
    width,
    zIndex,
  };

  // Add escape key handling to close the drawer
  useEffect(() => {
    if (escapeKey) {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && open) {
          onClose();
        }
      };
      window.addEventListener('keydown', handleEscape);
      return () => {
        window.removeEventListener('keydown', handleEscape);
      };
    }
  }, [open, onClose, escapeKey]);

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '20%' }}
      transition={{
        type: 'tween',
        duration: 0.2,
        ease: 'easeOut',
      }}
      className="pointer-events-none fixed inset-0"
    >
      <div className="absolute inset-0 overflow-hidden">
        <div
          className={twMerge(
            'pointer-events-auto absolute inset-y-0 right-0',
            className,
            isDragging && 'select-none'
          )}
          style={computedStyle}
        >
          <div className="relative h-full">
            <motion.div
              key={uuid || 'static'}
              animate={
                uuid
                  ? {
                      x: [0, -3, 0],
                      transition: {
                        duration: 0.3,
                        times: [0, 0.5, 1],
                        ease: 'easeOut',
                      },
                    }
                  : {}
              }
              className="h-full"
            >
              <div
                className="bg-chariot-background-primary text-chariot-text-primary border-chariot-stroke h-full overflow-y-auto border-l-2"
                data-testid="drawer-content"
              >
                <button
                  onClick={onClose}
                  className="border-chariot-stroke bg-chariot-background-primary hover:border-chariot-brand-primary absolute top-6 -left-14 flex size-10 items-center justify-center rounded-full border-2 transition-colors"
                >
                  <span className="sr-only">Close panel</span>
                  <X className="text-chariot-text-primary size-5" />
                </button>

                <div
                  onMouseDown={startResizing}
                  className="border-chariot-stroke bg-chariot-background-primary hover:border-chariot-brand-primary absolute top-1/2 -left-10 flex h-20 w-6 -translate-y-1/2 cursor-ew-resize items-center justify-center rounded-full border-2 transition-colors"
                  role="separator"
                  aria-label="Resize drawer"
                >
                  <div className="flex flex-row gap-1 p-2">
                    <div className="bg-chariot-text-primary h-6 w-0.5 rounded-full" />
                    <div className="bg-chariot-text-primary h-6 w-0.5 rounded-full" />
                  </div>
                </div>

                {children}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

interface DrawersProps {
  children: React.ReactNode;
  disableBackdrop?: boolean;
}

export const Drawers: React.FC<DrawersProps> = ({
  children,
  disableBackdrop = false,
}) => {
  const childArray = React.Children.toArray(children);
  const lastTwoDrawers = childArray.slice(-2);
  const parentDrawer = lastTwoDrawers[0];
  const childDrawer = lastTwoDrawers[1];

  const hasParentAndChild =
    React.isValidElement<DrawerProps>(parentDrawer) &&
    React.isValidElement<DrawerProps>(childDrawer) &&
    parentDrawer.props.open &&
    childDrawer.props.open;

  return (
    <>
      {/* Parent Drawer */}
      <AnimatePresence>
        {React.isValidElement<DrawerProps>(parentDrawer) &&
          parentDrawer.props.open && (
            <Drawer
              {...parentDrawer.props}
              key="parent-drawer"
              variant="parent"
              escapeKey={!hasParentAndChild}
              style={{
                ...parentDrawer.props.style,
                width: hasParentAndChild ? 'calc(100% - 60px)' : '60%',
              }}
            />
          )}
      </AnimatePresence>

      {/* Backdrop */}
      <AnimatePresence>
        {hasParentAndChild && !disableBackdrop && (
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => {
              if (React.isValidElement<DrawerProps>(childDrawer)) {
                childDrawer.props.onClose();
              }
            }}
            className="bg-chariot-shade/50 pointer-events-auto fixed inset-0"
          />
        )}
      </AnimatePresence>

      {/* Child Drawer */}
      <AnimatePresence>
        {React.isValidElement<DrawerProps>(childDrawer) &&
          childDrawer.props.open && (
            <Drawer
              {...childDrawer.props}
              key={`child-drawer-${childDrawer.key ?? 'child'}`}
              variant="child"
              escapeKey={true}
              style={{
                width: 'calc(50% - 60px)',
                zIndex: 102,
              }}
            />
          )}
      </AnimatePresence>
    </>
  );
};
